import { Component } from '@theme/component';
import { appendSubClassCartAudit, fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * @typedef {Object} Metafield
 * @property {string} key
 * @property {any} value
 */

/**
 * @typedef {Object} VariantMetafield
 * @property {string | number} id
 * @property {boolean} [isDigital]
 * @property {boolean} [isRental]
 * @property {string} [rentalExpDate]
 */

/**
 * @typedef {Object} ProductMetafields
 * @property {string} [publisherCode]
 * @property {VariantMetafield[]} [variants]
 */

/**
 * @typedef {Object} CartLineItem
 * @property {number} id
 * @property {number} quantity
 * @property {Record<string, string>} [properties]
 * @property {string | number | null} [selling_plan]
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) {
      event.preventDefault();
      form?.reportValidity();
      return;
    }

    const templateSuffixInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="hidden"][name="template_suffix"]'));
    if (templateSuffixInput && templateSuffixInput.value === 'textbook') {        
      const checkedBoxes = document.querySelectorAll('.purchase-checkbox:checked');
      if (checkedBoxes.length === 0) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {number} */
  #dsvInventoryCheckId = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    const initialVariantId = this.refs.variantId?.value;
    if (initialVariantId && this.hasAttribute('data-dsv-flag')) {
      const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
      const variantAvailable = Boolean(button && !button.hasAttribute('data-sold-out') && !button.disabled);
      this.#applyDsvInventoryGuard(initialVariantId, variantAvailable);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled === true) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) return;

    // fallback to check validity
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    let requestBody = formData;
    // Check for digital products and handle fees BEFORE adding main product
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const variantIdEntry = formData.get('id');
    const variantId = variantIdEntry ? String(variantIdEntry) : null;
    const mainVariantId = variantId;
    
    // Get metafield data from the JSON script tag
    const metafieldsData = this.#getProductMetafields();
    const isDigital = variantId ? this.#isVariantDigital(variantId, metafieldsData) : false;
    const rentalExpirationDate = variantId ? this.#isRental(variantId, metafieldsData) : null;
    const publisherCode = metafieldsData?.publisherCode || null;

    if (rentalExpirationDate) {
      formData.append('properties[_rental_flag]', 'true');
      formData.append('properties[_rental_expiration_date]', String(rentalExpirationDate));
      formData.append('properties[Rent Due Date]', String(rentalExpirationDate));
      // Lock checkout/express before /cart/add.js — closes the pre-RC race.
      window.rentalCollateralManager?.notifyRentalAddStarting?.();
    }
    
    // Add digital marker to form data if this is a digital product
    if (isDigital) {
      formData.append('properties[_is_digital]', 'true');
      formData.append('properties[_publisher_code]', publisherCode || '');

      const api_url = window.digital_product_config?.api_url ?? ''
      const store_number = window.digital_product_config?.store_number ?? ''

      if (api_url && store_number && publisherCode) {
        try {
          const apiResponse = await fetch(`${api_url}?storeNumber=${store_number}&downloadprovidercode=${publisherCode}`);
          const result = await apiResponse.json();
          if (result && result.length > 0 && result[0].isApplied) {
            const feeLineItem = await this.#buildEcommerceFeeLineItem(quantity, publisherCode);
            if (feeLineItem) {
              requestBody = this.#buildMultiItemFormData(formData, quantity, feeLineItem);
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
      // const {shouldAddFee, publisherCode: feePublisherCode} = await this.#shouldAddDigitalFee(publisherCode);
      // console.log('Should add fee:', shouldAddFee,feePublisherCode);
    }

    if (variantId && this.#isPdpDdcsRentalRefundVariant(variantId, metafieldsData)) {
      try {
        await (window.__pdpDdcsRentalReadyPromise ?? Promise.resolve());
      } catch {
        /* ignore */
      }
      const getPdp = window.getPdpRentalCampusTermLineProperties;
      if (typeof getPdp === 'function') {
        const ddcs = getPdp();
        formData.append(
          'properties[_rental_refund_grace_period]',
          String(ddcs._rental_refund_grace_period ?? '')
        );
        formData.append('properties[_rental_refund_date]', String(ddcs._rental_refund_date ?? ''));
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      requestBody.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: requestBody });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = mainVariantId;

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) {
            if (addToCartTextError) {
              addToCartTextError.classList.remove('hidden');
              const textNode = addToCartTextError.childNodes[2];
              if (textNode) {
                textNode.textContent = Theme.translations?.cart_error_generic || 'Something went wrong. Please try again.';
              } else {
                addToCartTextError.appendChild(document.createTextNode(Theme.translations?.cart_error_generic || 'Something went wrong. Please try again.'));
              }
              this.#timeout = setTimeout(() => {
                if (addToCartTextError) addToCartTextError.classList.add('hidden');
              }, 10000);
            }
            return;
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          const mainSubClass = formData.get('properties[_sub_class]');
          if (mainSubClass) {
            appendSubClassCartAudit(String(mainSubClass), 'product-form.js', 392);
          }
        }
      })
      .catch(() => {
        // Show user-facing error only; no console logging
        try {
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            const message = Theme.translations?.cart_error_generic || 'Something went wrong. Please try again.';
            if (textNode) {
              textNode.textContent = message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(message));
            }
            if (this.refs.liveRegion) this.#setLiveRegionText(message);
            this.#timeout = setTimeout(() => {
              if (addToCartTextError) {
                addToCartTextError.classList.add('hidden');
                if (this.refs.liveRegion) this.#clearLiveRegionText();
              }
            }, 10000);
          }
        } catch (_) {
          // Silently ignore so no console error
        }
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Build multi-item form data to add the main item with a fee in one request.
   * @param {FormData} formData - Original form data
   * @param {number} quantity - Quantity of main item
   * @param {{id: number, quantity: number, properties: Record<string, string>}} feeLineItem
   * @returns {FormData}
   */
  #buildMultiItemFormData(formData, quantity, feeLineItem) {
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && match[1]) {
        properties[match[1]] = String(value);
      }
    }

    const sellingPlanEntry = formData.get('selling_plan');
    const idEntry = formData.get('id');
    const id = idEntry ? Number(idEntry) : null;
    if (!id) {
      throw new Error('Form ID is required');
    }
    /** @type {CartLineItem} */
    const mainItem = {
      id,
      quantity,
      properties,
      selling_plan: sellingPlanEntry ? String(sellingPlanEntry) : null,
    };

    const multiItemFormData = new FormData();
    const items = [mainItem, feeLineItem];

    items.forEach((/** @type {CartLineItem} */ item, /** @type {number} */ index) => {
      if (!item?.id) return;
      multiItemFormData.append(`items[${index}][id]`, String(item.id));
      multiItemFormData.append(`items[${index}][quantity]`, String(item.quantity));

      if (item.selling_plan) {
        multiItemFormData.append(`items[${index}][selling_plan]`, String(item.selling_plan));
      }

      if (item.properties) {
        Object.entries(item.properties).forEach(([key, value]) => {
          multiItemFormData.append(`items[${index}][properties][${key}]`, String(value));
        });
      }
    });

    return multiItemFormData;
  }
  /**
   * @param {string | number} variantId
   * @returns {Promise<Metafield[]>}
   */
  async fetchMetafields(variantId) {
    if (typeof window !== 'undefined' && window.fetchMetafieldsOnce) {
      return window.fetchMetafieldsOnce(variantId);
    }

    const proxyPath = '/apps/shopifyData/getMetafields';
    const url = `${proxyPath}?query=gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Metafields');
      }
    } catch (error) {
      console.error('Error fetching Metafields:', error);
      throw error;
    }
  }
  /**
   * Add digital fee as product for online store
   * @param {number} quantity - Quantity
   * @param {string} feePublisherCode - feePublisherCode
   */
  async #buildEcommerceFeeLineItem(quantity, feePublisherCode) {
    const feeProductHandle = window.digital_product_config?.product_handle ?? '';

    try {
      // Get the fee product to find its variant ID
      const productResponse = await fetch(`/products/${feeProductHandle}.js`);
      if (!productResponse.ok) {
        console.error('Digital delivery fee product not found');
        return null;
      }
      
      const feeProduct = /** @type {{variants: Array<{id: number | string}>}} */ (await productResponse.json());
      const feeVariantId = feeProduct.variants[0]?.id;
      
      if (!feeVariantId) {
        console.error('No variant found for digital delivery fee product');
        return null;
      }
      const metafields = await this.fetchMetafields(feeVariantId);
      /**
       * @param {string} key
       * @returns {string}
       */
      const getMetafieldValue = (key) => {
          const metafield = metafields.find((/** @type {Metafield} */ m) => m.key === key);
          return metafield ? String(metafield.value) : ''; 
      };

      const feeLineItem = {
        id: Number(feeVariantId),
        quantity,
        properties: {
          '_digital_delivery_fee': 'true',
          '_is_digital': 'true',
          '_fee_type': 'online_store',
          '_department': getMetafieldValue('department'),
          '_sub_department': getMetafieldValue('sub_department'),
          '_class': getMetafieldValue('class'),
          '_sub_class': getMetafieldValue('sub_class'),
        },
      };
      appendSubClassCartAudit(feeLineItem.properties._sub_class, 'product-form.js', 573);
      return feeLineItem;
    } catch (error) {
      console.error('Error building digital fee product:', error);
      return null;
    }
  }

  /**
   * Get product metafields from JSON script tag
   * @returns {ProductMetafields | null} Metafields data
   */
  #getProductMetafields() {
    try {
      const productId = this.dataset.productId;
      const scriptTag = document.querySelector(`script[data-product-metafields="${productId}"]`);
      if (scriptTag && scriptTag.textContent) {
        return /** @type {ProductMetafields} */ (JSON.parse(scriptTag.textContent));
      }
    } catch (error) {
      console.error('Error parsing product metafields:', error);
    }
    return null;
  }

  /**
   * Check if a variant is digital
   * @param {string | null} variantId - Variant ID
   * @param {ProductMetafields | null} metafieldsData - Metafields data
   * @returns {boolean}
   */
  #isVariantDigital(variantId, metafieldsData) {
    if (!metafieldsData || !variantId) return false;
    
    const variant = metafieldsData.variants?.find((/** @type {VariantMetafield} */ v) => String(v.id) === String(variantId));
    return variant?.isDigital || false;
  }

  /**
   * Check if a variant is rental
   * @param {string | null} variantId - Variant ID
   * @param {ProductMetafields | null} metafieldsData - Metafields data
   * @returns {string | null}
   */
  #isRental(variantId, metafieldsData) {
    if (!metafieldsData || !variantId) return null;
    
    const variant = metafieldsData.variants?.find((/** @type {VariantMetafield} */ v) => String(v.id) === String(variantId));
    if(variant?.isRental && variant?.rentalExpDate) {
      return variant.rentalExpDate;
    }
    return null;
  }

  /**
   * Non-buy rental variant: attach DDCS campus/term refund props from pdp-latest-term.js (same as CMRP keys).
   * @param {string | null} variantId
   * @param {ProductMetafields | null} metafieldsData
   * @returns {boolean}
   */
  #isPdpDdcsRentalRefundVariant(variantId, metafieldsData) {
    if (!metafieldsData || !variantId) return false;
    const variant = metafieldsData.variants?.find((/** @type {VariantMetafield} */ v) => String(v.id) === String(variantId));
    if (!variant) return false;
    if (variant.isRental === true || variant.isRental === 'true') return true;
    const exp = variant.rentalExpDate;
    return exp != null && String(exp).trim() !== '' && String(exp).toLowerCase() !== 'null';
  }

  /**
   * Calculate required digital fees based on current cart
   * @returns {Promise<number>} Number of fees needed
   */
  async #calculateRequiredFees() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // We need to check each item's variant ID against our metafields data
      // For now, we'll count items that have the digital delivery property
      const digitalItemCount = cartData.items.filter((/** @type {CartLineItem} */ item) => {
        // Check if item has digital marker in properties (set when added)
        return item.properties && item.properties['_is_digital'] === 'true';
      }).reduce((/** @type {number} */ sum, /** @type {CartLineItem} */ item) => sum + item.quantity, 0);
      
      const existingFeeCount = cartData.items.filter((/** @type {CartLineItem} */ item) =>
        item.properties && item.properties['_digital_delivery_fee'] === 'true'
      ).reduce((/** @type {number} */ sum, /** @type {CartLineItem} */ item) => sum + item.quantity, 0);
      
      return Math.max(0, digitalItemCount - existingFeeCount);
    } catch (error) {
      console.error('Error calculating required fees:', error);
      return 0;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;


    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      // Instead of using morph, let's manually update the button content and state
      const shouldBeDisabled = /** @type {HTMLButtonElement} */ (newAddToCartButton).disabled;
      
      // Update button text content
      const currentText = currentAddToCartButton.querySelector('.add-to-cart-text__content');
      const newText = newAddToCartButton.querySelector('.add-to-cart-text__content');
      if (currentText && newText) {
        currentText.textContent = newText.textContent;
      }
      
      // Update button disabled state
      currentAddToCartButton.disabled = shouldBeDisabled;
      
      // Update container state
      if (shouldBeDisabled) {
        addToCartButtonContainer.disable();
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        addToCartButtonContainer.enable();
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Update the variant ID
    const newVariantId = event.detail.resource?.id ?? '';
    variantId.value = newVariantId;

    // Update digital product data attributes
    if (event.detail.resource) {
      // Get updated metafields data for the variant
      const metafieldsData = this.#getProductMetafields();
      if (metafieldsData) {
        const isDigital = this.#isVariantDigital(String(event.detail.resource.id), metafieldsData);
        addToCartButtonContainer?.setAttribute('data-is-digital', isDigital.toString());
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    const variantAvailable =
      event.detail.resource != null && event.detail.resource.available !== false;
    const variantIdForGuard =
      event.detail.resource?.id != null ? String(event.detail.resource.id) : '';
    this.#applyDsvInventoryGuard(variantIdForGuard, variantAvailable);
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  #isDsvFlagEnabled() {
    return this.dataset.dsvFlag === 'true';
  }

  /**
   * @param {string | undefined} name
   */
  #isDsvInventoryLocation(name) {
    return typeof name === 'string' && name.trim().toUpperCase().includes('DSV');
  }

  /**
   * @param {{ available?: number } | undefined} location
   */
  #getAvailableQuantity(location) {
    const available = location?.available;
    return typeof available === 'number' && Number.isFinite(available) ? available : 0;
  }

  /**
   * Block add-to-cart when only DSV location(s) have sellable inventory and all
   * non-DSV locations have zero available.
   *
   * @param {Array<{ name?: string, available?: number }> | undefined} locations
   */
  #shouldBlockForDsvOnlyInventory(locations) {
    if (!Array.isArray(locations) || locations.length === 0) return false;

    const dsvLocations = locations.filter((location) =>
      this.#isDsvInventoryLocation(location?.name)
    );
    const nonDsvLocations = locations.filter(
      (location) => !this.#isDsvInventoryLocation(location?.name)
    );

    const dsvHasAvailable = dsvLocations.some(
      (location) => this.#getAvailableQuantity(location) > 0
    );
    if (!dsvHasAvailable) return false;

    const nonDsvHasAvailable = nonDsvLocations.some(
      (location) => this.#getAvailableQuantity(location) > 0
    );
    return !nonDsvHasAvailable;
  }

  /**
   * @param {string} variantId
   * @param {boolean} variantAvailable
   */
  async #applyDsvInventoryGuard(variantId, variantAvailable) {
    if (!variantId || !this.hasAttribute('data-dsv-flag')) return;

    if (this.#isDsvFlagEnabled()) {
      this.#setDsvInventoryBlocked(false, variantAvailable);
      return;
    }
    
    const checkId = ++this.#dsvInventoryCheckId;

    try {
      const response = await fetch(`/apps/inventory?variant_id=${encodeURIComponent(variantId)}`);
      if (!response.ok || checkId !== this.#dsvInventoryCheckId) return;
      
      const data = await response.json();
      if (data.error || checkId !== this.#dsvInventoryCheckId) return;

      const shouldBlock = this.#shouldBlockForDsvOnlyInventory(data.locations);
      this.#setDsvInventoryBlocked(shouldBlock, variantAvailable);
    } catch {
      // Fail open — don't block add to cart on API errors
    }
  }

  #setDsvInventoryNoticeVisible(visible) {
    const notice = this.querySelector('[data-dsv-inventory-notice]');
    if (!notice) return;

    if (visible) {
      notice.removeAttribute('hidden');
      return;
    }

    if (this.#isDsvFlagEnabled()) return;

    notice.setAttribute('hidden', '');
  }

  /**
   * @param {boolean} blocked
   * @param {boolean} [variantAvailable]
   */
  #setDsvInventoryBlocked(blocked, variantAvailable = true) {
    const { addToCartButtonContainer } = this.refs;
    const button = addToCartButtonContainer?.refs.addToCartButton;
    if (!addToCartButtonContainer || !button) return;

    if (blocked) {
      addToCartButtonContainer.disable();
      addToCartButtonContainer.setAttribute('data-dsv-inventory-blocked', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      this.#setDsvInventoryNoticeVisible(true);
      return;
    }

    addToCartButtonContainer.removeAttribute('data-dsv-inventory-blocked');
    this.#setDsvInventoryNoticeVisible(false);
    if (variantAvailable && !button.hasAttribute('data-sold-out')) {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}